import React from 'react';
import { useHotLeads } from '../hooks/useData';
import { formatDistanceToNow } from 'date-fns';
import { Flame, Mail, Link2, Phone, MessageCircle } from 'lucide-react';

export function HotLeads() {
  const { leads, loading } = useHotLeads();

  return (
    <div className="page">
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
        <Flame size={18} color="var(--green)" />
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Hot Leads</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
            Visitors who gave consent — ready for outreach
          </div>
        </div>
        <span className="badge badge-green" style={{ marginLeft: 'auto' }}>
          {leads.length} leads
        </span>
      </div>

      {loading ? (
        <div className="empty"><div className="empty-title">Loading leads...</div></div>
      ) : leads.length === 0 ? (
        <div className="empty">
          <Flame size={32} style={{ opacity: 0.3 }} />
          <div className="empty-title">No hot leads yet</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            When visitors consent via the popup, they'll appear here
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {leads.map((lead: any) => (
            <LeadCard key={lead.sessionId} lead={lead} />
          ))}
        </div>
      )}
    </div>
  );
}

function LeadCard({ lead }: { lead: any }) {
  const channelIcon: Record<string, any> = {
    email:    <Mail size={13} />,
    linkedin: <Link2 size={13} />,
    phone:    <Phone size={13} />,
    whatsapp: <MessageCircle size={13} />,
  };

  return (
    <div className="card" style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {/* Left — identity */}
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          {/* Avatar */}
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'var(--bg3)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 14, fontWeight: 700,
            color: 'var(--accent2)', flexShrink: 0,
          }}>
            {(lead.person ?? lead.company ?? '?').charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {lead.person ?? <span style={{ color: 'var(--text3)' }}>Unknown</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 2 }}>
              {lead.company ?? '—'}
              {lead.industry && ` · ${lead.industry}`}
            </div>
            {lead.searchTerm && (
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                🔍 Searched: <span style={{ color: 'var(--text2)' }}>"{lead.searchTerm}"</span>
              </div>
            )}
          </div>
        </div>

        {/* Right — meta */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
            {lead.consentedAt ? formatDistanceToNow(new Date(lead.consentedAt), { addSuffix: true }) : '—'}
          </div>
          <div style={{ marginTop: 4 }}>
            <span style={{
              fontSize: 22, fontWeight: 800, fontFamily: 'var(--mono)',
              color: lead.score >= 70 ? 'var(--green)' : lead.score >= 40 ? 'var(--amber)' : 'var(--text3)',
            }}>
              {lead.score}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 2 }}>score</span>
          </div>
        </div>
      </div>

      {/* Channels */}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        {lead.channels?.map((ch: string) => (
          <div key={ch} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '4px 10px', borderRadius: 6,
            background: 'var(--bg3)', fontSize: 12, fontWeight: 500,
            color: 'var(--text2)',
          }}>
            {channelIcon[ch]}
            {ch}
          </div>
        ))}
        {lead.visitNumber > 1 && (
          <div className="badge badge-blue">Visit #{lead.visitNumber}</div>
        )}
        {lead.outreachSent && (
          <div className="badge badge-purple">✓ Outreach sent</div>
        )}
      </div>
    </div>
  );
}
